import {
    MODE_DELIVERY,
    MODE_ONSITE,
    MODE_TAKEAWAY,
} from "../constants/constants";
import {
    CategoryItem,
    Ingredient,
    OrderItem,
    PurchaseOffers,
    SelectedArticle,
    TypeOrderPrice,
} from "../interfaces/Catalogue";
import { PanierDiscount } from "../interfaces/loyality.interface";
import { isPurchaseOfferActive } from "./loyaltyUtils";

export const getTotalOrder = (categories: CategoryItem[]): number => {

    const calculatedTotal =
        categories?.length > 0
            ? categories?.reduce((total: number, category) => {
                if (category?.purchaseOffers && isPurchaseOfferActive(category.purchaseOffers)) {
                    const { paid } = getPaidFreeCounts(category.purchaseOffers, category?.quantity || 0);
                    return (
                        total + (category?.finalUnitPrice || 0) * paid
                    );
                }
                return (
                    total + (category?.finalUnitPrice || 0) * (category?.quantity || 0)
                );
            }, 0)
            : 0;

    return parseFloat(calculatedTotal.toFixed(2));
};

// PRICE:
export const findPriceByTypeOrder = (
    typeOrderCategory: TypeOrderPrice[],
    typeOrder: number
): number => {
    const item = typeOrderCategory?.find(
        (orderCategory) => orderCategory.type === typeOrder
    );
    return item?.price || 0;
};

export const getPriceByTypeOrderForComposition = (
    typeOrder: number,
    articleComposition: Ingredient
): number => {
    switch (typeOrder) {
        case MODE_ONSITE: // Sur Place
            return articleComposition.priceOnSite ?? 0;
        case MODE_TAKEAWAY: // A Emporter
            return articleComposition.priceEmporter ?? 0;
        case MODE_DELIVERY: // Livraison
            return articleComposition.priceDelivery ?? 0;
        default:
            return 0;
    }
};

export const centimesToEuros = (centimes: number): number => centimes / 100;

export const calculatediscountedPrice = (
    priceByTypeOrder: number,
    rewardDiscountType: number,
    rewardDiscountValue: number
): number => {
    const discountedPrice =
        rewardDiscountType === 0
            ? priceByTypeOrder - (priceByTypeOrder * rewardDiscountValue) / 100
            : priceByTypeOrder - rewardDiscountValue;

    return parseFloat((discountedPrice < 0 ? 0 : discountedPrice).toFixed(2));
};

export const calculateCartDiscount = (
    price: number,
    panierDiscount: PanierDiscount
): number => {
    return panierDiscount.cartRewardDiscountType === 0
        ? (price * panierDiscount.cartRewardDiscountValue) / 100 // Percentage discount
        : panierDiscount.cartRewardDiscountValue; // Fixed amount discount
};


export const calculateSelectedArticlesPrice = (
    selectedArticles: SelectedArticle[] = [],
    orderType: number,
    afterDiscount = false,
    excludeFree = true
): number =>
    selectedArticles.reduce((sum, article) => {
        if (excludeFree && article.selecteMenuItem.isFree) {
            return sum;
        }


        return (
            sum +
            getSelectedArticlePrice(
                article,
                afterDiscount,
                orderType
            )
        );
    }, 0);

export const calculateMenuFinalPrice = (
    order: OrderItem[],
    orderType: number,
    afterDiscount = false
): number =>
    order.reduce(
        (total, item) =>
            total +
            calculateSelectedArticlesPrice(
                item.selectedArticles,
                orderType,
                afterDiscount
            ),
        0
    );


/**
 * Compute the full price of a single SelectedArticle
 * (base + ingredients) × requested quantity
 */
const getSelectedArticlePrice = (
    selectedArticle: SelectedArticle,
    afterDiscount: boolean,
    orderType: number

): number => {
    const { selecteMenuItem, selectedIngredients } = selectedArticle;

    const priceByOrder =
        findPriceByTypeOrder(selecteMenuItem?.typeOrderPrices, orderType) ?? 0;

    const basePrice = afterDiscount
        ? selecteMenuItem?.discountedPrice ?? priceByOrder
        : priceByOrder;

    const ingredientsPrice =
        selectedIngredients?.reduce(
            (sum, { ingredient, quantity = 1 }) =>
                sum +
                getPriceByTypeOrderForComposition(orderType, ingredient) * quantity,
            0
        ) ?? 0;

    return (basePrice + ingredientsPrice) * (selecteMenuItem?.quantity ?? 1);
};


export const calculateTotalArticlesPrice = (
    categories: CategoryItem[],
    orderType: number,
    afterDiscount: boolean
): number => {
    let subTotal = 0;

    categories?.forEach((categoryItem) => {
        subTotal +=
            calculateItemTotalPrice(categoryItem, orderType, afterDiscount) || 0;
    });

    return subTotal;
};

export const calculateItemTotalPrice = (
    categoryItem: CategoryItem,
    orderType: number,
    afterDiscount: boolean
): number | null => {
    let totalPrice: number;

    // isMenu
    if (categoryItem.order) {
        const finalUnitPrice = calculateMenuFinalPrice(
            categoryItem.order,
            orderType,
            afterDiscount
        );
        totalPrice = finalUnitPrice * (categoryItem.quantity ?? 0);
    } else {
        // isArticle

        const { paid } = getPaidFreeCounts(
            categoryItem.purchaseOffers,
            categoryItem.quantity || 0
        );

        if (afterDiscount) {
            totalPrice =
                (categoryItem.discountedPrice || categoryItem.finalUnitPrice || 0) *
                paid;
        } else {
            totalPrice =
                (categoryItem.finalUnitPrice ?? 0) * paid;
        }
    }

    return Math.max(totalPrice, 0);
};

export const calculateNewTotalUserPts = (
    totalLoyaltyPtsConsomed: number,
    selecteMenuItem: { rewardTotalPtRequired: number; quantity?: number },
    orderCategoryItem?: { quantity?: number }
): number => {
    return (
        (totalLoyaltyPtsConsomed || 0) +
        selecteMenuItem.rewardTotalPtRequired *
        (selecteMenuItem.quantity || 0) *
        (orderCategoryItem?.quantity || 0)
    );
};

export const calculateArticleTotalPriceWithOffers = (
    purchaseOffers: PurchaseOffers,
    finalUnitPrice: number,
    articleCount: number
): number => {
    const { paid } = getPaidFreeCounts(purchaseOffers, articleCount);
    return paid * finalUnitPrice;
};


export const getPaidFreeCounts = (
    offers: PurchaseOffers,
    quantity: number
): { paid: number; free: number } => {

    if (!isPurchaseOfferActive(offers)) {
        return { paid: quantity, free: 0 };
    }

    const nbPurchased = Number(offers.nbPurchased);
    const nbOffered = Number(offers.nbOffered);

    if (nbPurchased === 0 || nbOffered === 0) {
        return { paid: quantity, free: 0 };
    }

    const blockSize = nbPurchased + nbOffered;
    const fullBlocks = Math.floor(quantity / blockSize);
    const remainder = quantity % blockSize;

    const paid = fullBlocks * nbPurchased + Math.min(remainder, nbPurchased);
    const free = fullBlocks * nbOffered + Math.max(0, remainder - nbPurchased);

    return { paid, free };
};



// /**
//  * Calculates the number of paid (chargeable) items for a given quantity,
//  * applying purchase offers like "Buy X Get Y Free" if applicable.
//  */
// export function calculatePaidQuantity(
//     purchaseOffers: PurchaseOffers,
//     quantity: number
// ): number {
//     // TODO: Uncument this function logic
//     if (!purchaseOffers) return quantity;

//     // handle beginDate and endDate validation
//     const currentDate = new Date();
//     if (purchaseOffers.beginDate && new Date(purchaseOffers.beginDate) > currentDate
//         || purchaseOffers.endDate && new Date(purchaseOffers.endDate) < currentDate) {
//         return quantity;
//     }

//     const { nbPurchased, nbOffered } = purchaseOffers;

//     if (nbPurchased > 0) {
//         const blockSize = nbPurchased + nbOffered;
//         const fullBlocks = Math.floor(quantity / blockSize);
//         const remainder = quantity % blockSize;

//         const paidQuantity = fullBlocks * nbPurchased + Math.min(remainder, nbPurchased);
//         return paidQuantity;
//     }

//     return quantity;
// }

// export function calculatePaidAndFreeQuantity(
//     purchaseOffers: PurchaseOffers,
//     quantity: number
// ): { paid: number; free: number } {
//     const paid = calculatePaidQuantity(purchaseOffers, quantity);
//     return {
//         paid,
//         free: quantity - paid
//     };
// }

// /**
//  * Calculates the total price for a single article,
//  * based on paid quantity and unit price.
//  */
// export function calculateArticleTotalPrice(
//     categoryItem: CategoryItem,
//     finalUnitPrice: number,
//     quantity: number
// ): number {
//     const paidQuantity = calculatePaidQuantity(categoryItem.purchaseOffers, quantity);
//     return +(paidQuantity * finalUnitPrice).toFixed(2);
// }

// export const tagFreeItems = (
//     offers: OfferGroup[],
//     orderTypeId: number,
//     menuQuantity: number
// ): OfferGroup[] => {
//     console.log("🚀 ~ tagFreeItems - menuQuantity:", menuQuantity);
//     return offers.map((offerGroup) => {

//         const { purchaseOffers, selectedArticles } = offerGroup;
//         const { nbPurchased, nbOffered } = purchaseOffers;

//         // Calculer la quantité totale d'articles multipliée par la quantité du menu
//         const totalArticles = selectedArticles.reduce(
//             (acc, article) => acc + (article.selecteMenuItem.quantity ?? 0),
//             0
//         ) * menuQuantity;

//         // Calculer le nombre d'articles payés en arrondissant au supérieur
//         const nbPaid = Math.ceil(totalArticles / (nbPurchased + nbOffered));

//         // Calculer les articles gratuits correspondants
//         let eligibleFreeCount = nbPaid * nbOffered;

//         // Ajuster si la somme dépasse le total réel d'articles
//         if (nbPaid + eligibleFreeCount > totalArticles) {
//             eligibleFreeCount = totalArticles - nbPaid;
//         }

//         // Copier et trier les articles par prix croissant
//         const articlesCopy = [...selectedArticles];
//         const sortedArticles = articlesCopy.sort((a, b) => {
//             const priceA =
//                 findPriceByTypeOrder(a.selecteMenuItem.typeOrderPrices, orderTypeId) || 0;
//             const priceB =
//                 findPriceByTypeOrder(b.selecteMenuItem.typeOrderPrices, orderTypeId) || 0;
//             return priceA - priceB;
//         });

//         const updatedArticles: SelectedArticle[] = [];
//         let remainingFreeItems = eligibleFreeCount;

//         for (const article of sortedArticles) {
//             const totalQuantity = (article.selecteMenuItem.quantity ?? 0) * menuQuantity;

//             // Attribuer le maximum d'items gratuits possible à cet article
//             const freeQuantity = Math.min(totalQuantity, remainingFreeItems);
//             const paidQuantity = totalQuantity - freeQuantity;

//             remainingFreeItems -= freeQuantity;

//             updatedArticles.push({
//                 ...article,
//                 paidQuantity,
//                 freeQuantity,
//             });
//         }

//         const newOfferGroup = {
//             purchaseOffers,
//             selectedArticles: updatedArticles,
//         };
//         console.log("🚀 ~ returnoffers.map ~ newOfferGroup:", newOfferGroup);

//         return newOfferGroup;
//     });
// };

// export function processArticlesWithOffers(
//     newLevelArticles: SelectedArticle[],
//     typeOrder: number,
//     menuQuantity: number
// ): SelectedArticle[] {
//     console.log("🚀 ~ menuQuantity:", menuQuantity)
//     // 1. Filter articles with purchase offers
//     const articlesLevelWithOffers = newLevelArticles.filter(
//         (selectedArticle) => selectedArticle?.selecteMenuItem?.purchaseOffers
//     );

//     // 2. Group by purchaseOffers
//     const groupedByOffer = new Map<string, OfferGroup>();

//     articlesLevelWithOffers.forEach((article) => {
//         const offer = article.selecteMenuItem?.purchaseOffers;
//         if (!offer) return;
//         const offerKey = JSON.stringify(offer);

//         if (!groupedByOffer.has(offerKey)) {
//             groupedByOffer.set(offerKey, {
//                 purchaseOffers: offer,
//                 selectedArticles: [],
//             });
//         }

//         groupedByOffer.get(offerKey)!.selectedArticles.push(article);
//     });

//     const groupedOffersArray = Array.from(groupedByOffer.values());

//     // 3. Tag free items (custom logic)
//     const taggedOfferGroup: OfferGroup[] = tagFreeItems(
//         groupedOffersArray,
//         typeOrder,
//         menuQuantity
//     );

//     // 4. Flatten the selectedArticles
//     const flattenedArticles: SelectedArticle[] = taggedOfferGroup.flatMap(
//         (offerGroup) => offerGroup.selectedArticles
//     );

//     return flattenedArticles;
// }
